import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Fragment, Suspense, useEffect, useLayoutEffect, useState } from 'react';
import routes from "./routes/routes";
import MyNavbar from "./shared/Navbar/MyNavbar.jsx";
import Footer from './shared/Footer/Footer.jsx';
import { getUserSlice } from "./context/store/store";
import {
  restoreAuthenticatedUserSession,
  syncCurrentUser
} from './context/store/userSynchronization';
import ClientNotifications from './shared/Notifications/ClientNotifications';

function AuthenticatedSessionSync() {
  const location = useLocation();
  const { headers } = getUserSlice();
  const authorization = headers.Authorization;

  useEffect(() => {
    if (authorization) syncCurrentUser();
  }, [authorization, location.pathname]);

  return null;
}

// Route-level authentication boundary. Every private route is unmounted the
// instant `headers.Authorization` disappears (logout) or changes (a new
// session starts), independent of whatever local state that page still
// holds — this is what stops a stale render of another session's data from
// staying on screen after logout.
function ProtectedRoute({ sessionRestored, allowedRoles, children }) {
  const location = useLocation();
  const authorization = getUserSlice((state) => state.headers.Authorization);
  const role = getUserSlice((state) => state.role);
  const userSyncStatus = getUserSlice((state) => state.userSyncStatus);

  // Session restoration reads localStorage synchronously in a
  // useLayoutEffect before the first paint, but until that effect has run
  // `headers` is still the store's initial `{}`. Rendering nothing during
  // that (sub-frame) window avoids a false redirect-to-signin flash for an
  // already-authenticated user.
  if (!sessionRestored) return null;
  if (!authorization) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }
  if (Array.isArray(allowedRoles) && allowedRoles.length) {
    if (userSyncStatus === 'pending' || userSyncStatus === 'loading') return null;
    if (!allowedRoles.includes(role)) return <Navigate to="/account" replace />;
  }
  // Keyed on the token itself: a direct session change (user A's token
  // replaced by user B's, with no intermediate logged-out render) still has
  // to remount this subtree, or the private page's own component instance
  // — and every bit of local state it captured for A — survives untouched
  // under B. An unmount/remount is the only way to guarantee that doesn't
  // happen, since props/context changes alone do not reset local state.
  return <Fragment key={authorization}>{children}</Fragment>;
}

function App() {
  const [sessionRestored, setSessionRestored] = useState(false);

  useLayoutEffect(() => {
    restoreAuthenticatedUserSession();
    setSessionRestored(true);
  }, []);

  useEffect(() => {
    const onSessionStorage = (event) => {
      if (event.key !== 'token' && event.key !== null) return;
      restoreAuthenticatedUserSession();
    };
    window.addEventListener('storage', onSessionStorage);
    return () => window.removeEventListener('storage', onSessionStorage);
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthenticatedSessionSync />
      <ClientNotifications />
      <Routes>
        {routes.map((route, indexRoute) => {
          return (
            <Route
              path={route.path}
              element={
                <Suspense fallback={<span>Loading...</span>}>
                  {
                    route.path.includes('sign') ? (
                      <route.component />
                    ) : (
                      <>
                        <MyNavbar />
                        {route.private ? (
                          <ProtectedRoute sessionRestored={sessionRestored} allowedRoles={route.allowedRoles}>
                            <route.component />
                          </ProtectedRoute>
                        ) : (
                          <route.component />
                        )}
                        <Footer />
                      </>
                    )
                  }
                </Suspense>
              }
              key={indexRoute}>
            </Route>
          );
        })}
      </Routes>
    </BrowserRouter>
  );
}

export default App;
